import BlogCard from '#/components/common/BlogCard'
import Pagination from '#/components/common/Pagination'
import { useBlogs } from '../hooks/useBlogs'
import NoData from '#/components/common/NoData'
import blogDefaultImage from '#/assets/blog/blog-1.png'
import { getImageUrl } from '#/lib/imageUrl'
import { ScrollReveal, RevealItem } from '#/components/common/ScrollReveal'

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function formatDate(dateStr?: string) {
  const d = dateStr ? new Date(dateStr) : new Date()
  return {
    day: String(d.getDate()).padStart(2, '0'),
    month: MONTH_NAMES[d.getMonth()],
    year: String(d.getFullYear()),
  }
}

type BlogsListProps = {
  page?: number
  activeTags: string[]
  searchQuery?: string
  onPageChange?: (pageZeroIndexed: number) => void
}

export default function BlogsList({
  page = 1,
  activeTags,
  searchQuery,
  onPageChange,
}: BlogsListProps) {
  const tagSlugs = activeTags.length > 0 ? activeTags.join(',') : undefined

  const { data: blogsRes, isLoading } = useBlogs({
    page,
    limit: 6,
    tags: tagSlugs,
    search: searchQuery ? searchQuery.trim() : undefined,
  })

  const posts = blogsRes?.data || []
  const totalPages = blogsRes?.meta?.totalPages || 1
  const currentPageZeroIndexed = page > 0 ? page - 1 : 0

  return (
    <div className="flex w-full flex-col gap-8">
      {isLoading ? (
        <div className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[360px] w-full animate-pulse rounded-xl bg-neutral-200/50"
            />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <NoData
          title="No Blog Posts Found"
          description="Try adjusting your tag or search criteria."
        />
      ) : (
        <ScrollReveal
          key={`blogs-page-${page}-${tagSlugs || 'all'}-${searchQuery || ''}`}
          stagger={0.08}
          className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2"
        >
          {posts.map((post) => (
            <RevealItem key={post.id} direction="down" offset={16} scale={0.99}>
              <BlogCard
                id={post.slug}
                image={getImageUrl(post.coverImage, blogDefaultImage)}
                title={post.title}
                excerpt={post.excerpt}
                author={post.author?.name || 'Waallamah Scholar'}
                comments="0 Comments"
                readingTime={post.readingTime}
                date={formatDate(post.publishedAt)}
                tags={post.tags?.map((t) => t.name) || []}
              />
            </RevealItem>
          ))}
        </ScrollReveal>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            pageCount={totalPages}
            currentPage={currentPageZeroIndexed}
            onPageChange={(p) => onPageChange?.(p)}
          />
        </div>
      )}
    </div>
  )
}

export { BLOG_TAGS } from '#/features/blogs/data/blogPosts'
